import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import { DropArea } from '../../../components/form/DropArea/DropArea';
import { ACCEPTED_BILL_TYPES } from '../../../pages/BillPay/consts';
import DocPreview from '../../../assets/png/upload-doc-preview.png';
import { CloseButton } from '../../../DesignSystem/LiliCloseButton/CloseButton';
import { ReactComponent as ErrorIcon } from '../../../assets/svg/shared/icon-red-error.svg';

interface UploadDocFileUploadProps {
  onClose: () => void;
  onSubmit: (file: File) => void;
  isLoading: boolean;
}

const ErrorComponent = ({ errorMessage }: { errorMessage: string }) => (
  <div className='flex flex-row gap-[4px] mt-[16px] items-center'>
    <ErrorIcon className='h-[14px] w-[14px]' />
    <p className='font-metricR text-[14px] text-red02 mt-[2px]'>{errorMessage}</p>
  </div>
);
const PreviewComponent = ({ onClose, title }: { onClose: () => void; title: string }) => (
  <div className='basis-1/2 w-full h-full bg-bw00 flex flex-col items-center py-[21px] px-[25px] '>
    <div className='flex flex-col w-full items-end '>
      <CloseButton id='doc-upload-close-button' onClick={onClose} />
    </div>
    <p className='font-metricR text-[16px] text-bw08 mt-[54px]'>{title}</p>
    <img src={DocPreview} alt='doc-preview-img' className='max-w-[347px] max-h-[463px]' />
  </div>
);

const RulesComponent = ({ rules, title }: { rules: string[]; title: string }) => (
  <div className='flex flex-col gap-[4px] w-full border-[1px] border-bw03 rounded-lg py-[26px] px-[16px] mt-[16px] mb-[34px]'>
    <p className='font-metricRMedium text-[16px] text-bw08'>{title}</p>
    {rules.map((rule) => (
      <p key={rule} className='font-metricR text-[16px] text-bw08'>
        {rule}
      </p>
    ))}
  </div>
);

const FooterComponent = ({
  actionTitle,
  onSubmit,
  isLoading,
  isDisabled,
}: {
  isLoading: boolean;
  actionTitle: string;
  onSubmit: () => void;
  isDisabled: boolean;
}) => (
  <div className='flex flex-col mt-[24px] border-t-bw03 border-t-[1px] items-center h-[128px] pt-[24px]'>
    <div className=' w-full flex flex-col justify-center max-w-[370px] mt-[10px]'>
      <LiliButton
        id='upload-doc-submit-button'
        title={actionTitle}
        onClick={onSubmit}
        isLoading={isLoading}
        disabled={isDisabled}
      />
    </div>
  </div>
);

export const UploadDocFileUpload = ({ onSubmit, onClose, isLoading }: UploadDocFileUploadProps) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const maxFileSizeInMB: number = 15;
  const handleDropAreaFiles = (filesList: FileList) => {
    setErrorMessage(null);
    const list = Array.from(filesList);
    const fileToUpload = list[0];
    setSelectedFile(fileToUpload);
  };
  const rules = [
    t('UPLOAD_EIN_DOC.INPUT.RULE_1'),
    t('UPLOAD_EIN_DOC.INPUT.RULE_2'),
    t('UPLOAD_EIN_DOC.INPUT.RULE_3'),
    t('UPLOAD_EIN_DOC.INPUT.RULE_4'),
  ];
  const inputComponent = () => (
    <div className='basis-1/2 max-w-[50%] h-full justify-between flex flex-col pt-[57px] border-r-[1px] border-r-bw02'>
      <div className='flex flex-col  pl-[85px] pr-[125px] w-full  '>
        <p className='font-metricRSemibold text-[30px] text-bw08'>{t('UPLOAD_EIN_DOC.INPUT.TITLE')}</p>
        <RulesComponent title={t('UPLOAD_EIN_DOC.INPUT.RULES_TITLE')} rules={rules} />
        <DropArea
          onFilesChange={handleDropAreaFiles}
          maxFileSizeInMb={maxFileSizeInMB}
          acceptedTypes={ACCEPTED_BILL_TYPES}
          isMinimalMode
          acceptMultiFile={false}
          actionTitle={t('UPLOAD_EIN_DOC.INPUT.FILE_UPLOAD_ACTION')}
          onFileClear={() => setSelectedFile(null)}
          onError={setErrorMessage}
        />
        <p className='font-metricR text-[16px] text-bw07 bg-action00 w-full text-center py-[4px] px-[36px] mt-[16px] rounded-md '>
          {t('UPLOAD_EIN_DOC.INPUT.DISCLAIMER')}
        </p>
        {errorMessage && <ErrorComponent errorMessage={errorMessage} />}
      </div>
      <FooterComponent
        actionTitle={t('UPLOAD_EIN_DOC.INPUT.CTA')}
        onSubmit={() => onSubmit(selectedFile!!)}
        isLoading={isLoading}
        isDisabled={!selectedFile}
      />
    </div>
  );

  return (
    <div className='flex flex-row  items-center bg-bw00 overflow-hidden  w-[1160px]  rounded-md h-[calc(70vh)]'>
      {inputComponent()}
      <PreviewComponent onClose={onClose} title={t('UPLOAD_EIN_DOC.INPUT.EXAMPLE_TITLE')} />
    </div>
  );
};
